import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Collapse,
    Divider,
    Grid,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    Tooltip,
    Typography
} from "@mui/material";
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import moment from 'moment';
import { UAParser } from 'ua-parser-js';
import { useMutation, useQuery } from 'react-query';
import { CheckCircle, Delete } from '@mui/icons-material';
import useAuth from '../../hooks/useAuth';
import { useSnackbar } from 'notistack';

// Active Sessions Component
function ActiveSessions({ enabled = true }) {
    const { auth } = useAuth();

    // CUSTOM HOOK
    const axiosPrivate = useAxiosPrivate();

    // SNACKBAR
    const { enqueueSnackbar } = useSnackbar();

    // MUTATION & QUERY
    const {
        isLoading,
        data,
        refetch
    } = useQuery(["active_sessions"], () => {
        return axiosPrivate.get(`profile/devices`);
    },
        {
            refetchOnWindowFocus: false,
            enabled
        }
    );
    const {
        mutate,
        data: removeData,
        isLoading: removeIsLoading,
        error
    } = useMutation((refreshToken) => {
        return axiosPrivate.post(
            "/logout",
            {
                refresh_token: refreshToken
            },
            {
                withCredentials: true
            }
        );
    });

    if (data?.data?.data && auth) {
        const index = data?.data?.data?.findIndex((b) => auth?.refresh_token === b?.refresh_token);
        if (index !== -1)
            [data.data.data[0], data.data.data[index]] = [data.data.data[index], data.data.data[0]];
    }

    // USEEFFECT 
    useEffect(() => {
        if (removeData) {
            enqueueSnackbar(`Succesfully removed session.`, {
                variant: "success",
                preventDuplicate: true,
                autoHideDuration: 2000
            });
            refetch();
        }
    }, [refetch, removeData, enqueueSnackbar]);

    useEffect(() => {
        if (error) {
            enqueueSnackbar(
                error?.response?.data?.error?.message ||
                error?.message ||
                "Network Error!",
                {
                    variant: "error",
                    preventDuplicate: true,
                    autoHideDuration: 2000
                }
            );
        }
    }, [enqueueSnackbar, error]);

    // RENDERING
    return (
        <Box sx={{ bgcolor: 'background.paper' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {data?.data?.data?.map((session, index) => (
                    <React.Fragment key={session.id}>
                        <LoggedInSession
                            row={session}
                            remove={mutate}
                            removeIsLoading={removeIsLoading}
                        />
                        {index < data?.data?.data?.length - 1 && <Divider sx={{ opacity: 0.6 }} />}
                    </React.Fragment>
                ))}
                {(!data?.data?.data || data?.data?.data.length === 0) && !isLoading && (
                    <Box sx={{ py: 6, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.disabled" sx={{ fontWeight: 500 }}>
                            No active sessions found.
                        </Typography>
                    </Box>
                )}
            </Box>
        </Box>
    )
}
export default ActiveSessions;

// Logged-In Sessions Card - Premium Pass
function LoggedInSession(props) {
    const { row, remove, removeIsLoading } = props;
    const { auth } = useAuth();
    let parser = new UAParser(row?.user_agent);
    let parserResults = parser.getResult();
    const isCurrent = auth?.refresh_token === row.refresh_token;

    return (
        <Box
            sx={{
                p: { xs: 1.5, sm: 3 },
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                    bgcolor: 'rgba(0,0,0,0.01)',
                }
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: { xs: 1.5, sm: 2.5 }, minWidth: 0 }}>
                <Box
                    sx={{
                        width: 54,
                        height: 54,
                        borderRadius: 3,
                        bgcolor: isCurrent ? 'rgba(255, 170, 0, 0.1)' : 'grey.50',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '24px',
                        border: isCurrent ? '1.5px solid rgba(255, 170, 0, 0.2)' : "1px solid", borderColor: "divider",
                        flexShrink: 0
                    }}
                >
                    {parserResults.device.type === 'mobile' ? '📱' : '💻'}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, gap: { xs: 0.5, sm: 1.5 }, mb: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 800, color: 'secondary.main', wordBreak: 'break-word' }}>
                            {parserResults.browser.name} {parserResults.browser.version}
                            <Typography component="span" variant="caption" sx={{ color: 'text.disabled', mx: 0.8 }}>on</Typography>
                            {parserResults.os.name} {parserResults.os.version}
                        </Typography>
                        {isCurrent && (
                            <Box
                                sx={{
                                    bgcolor: 'success.main',
                                    color: 'white',
                                    fontSize: '9px',
                                    px: 1,
                                    py: 0.2,
                                    borderRadius: 1,
                                    fontWeight: 900,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px'
                                }}
                            >
                                This Device
                            </Box>
                        )}
                    </Box>

                    <Stack spacing={0.5}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                                IP: {row.ip_address}
                            </Typography>
                            <Divider orientation="vertical" flexItem sx={{ height: 12, my: 'auto' }} />
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                Last active {moment(row.updated_at).fromNow()}
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography variant="caption" sx={{ color: 'text.disabled', fontWeight: 700, fontSize: '10px', textTransform: 'uppercase' }}>Created:</Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>{moment(row.created_at).format('MMM D, YYYY HH:mm')}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography variant="caption" sx={{ color: 'text.disabled', fontWeight: 700, fontSize: '10px', textTransform: 'uppercase' }}>Expires:</Typography>
                                <Typography variant="caption" sx={{ color: moment(row.expires_at).isBefore(moment().add(1, 'day')) ? 'error.main' : 'text.secondary', fontWeight: 600 }}>
                                    {moment(row.expires_at).fromNow()} ({moment(row.expires_at).format('MMM D')})
                                </Typography>
                            </Box>
                        </Box>
                    </Stack>
                </Box>
                {!isCurrent && (
                    <Tooltip title="Terminate Session">
                        <IconButton
                            size="small"
                            onClick={() => remove(row.refresh_token)}
                            disabled={removeIsLoading}
                            sx={{
                                color: 'text.disabled',
                                '&:hover': {
                                    color: 'error.main',
                                    bgcolor: 'error.lighter',
                                },
                            }}
                        >
                            <Delete fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>
        </Box>
    );
}
